import { ENTITLEMENTS } from '../application-core/constants';
import GS from '../grafsync/grafsync';
import { v4 as uuidv4 } from 'uuid';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import Errored from '../core/Errored';
import { computeStatements, makeCancelable } from '../core/utils/utils';
import { globalStyles } from '../core/style/gstheme';
import ReificationDialog from './ReificationDialog';

const styles = Object.assign({}, globalStyles);

class ReificationContainer extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            applicableStatements: undefined,
            isErrored: undefined
        };
        this.cancellable = null;
    }

    loadData(iris) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, this.props.graph, false));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    async findStatements() {
        try {
            const { statements, property, value } = this.props;
            let loadedStatements = [];
            await this.loadData(statements).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            GS.handle("find-statements");
            for (let s of statements) {
                if (GS.isLoaded(s, this.props.graph)) {
                    const data = GS.data(s, this.props.graph);
                    loadedStatements.push(data);
                }
            }
            let applicableStatements = computeStatements(loadedStatements, property, value);
            let exists = applicableStatements.length > 0;
            if (!exists) {
                const statement = await this.addStatement();
                applicableStatements = [statement];
            }
            this.setState({ applicableStatements });
        }
        catch (e) {
            if (!this.cancelleable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    }

    componentDidMount() {
        this.findStatements();
    }

    addStatement = async () => {
        const slashHash = this.props.useSlashIRI ?
            this.props.baseIRI.endsWith('/') ?
                ''
                :
                '/'
            :
            '#';
        // Cater for hash in base IRI
        const baseIRI = this.props.baseIRI.indexOf('#') > 0 ?
            this.props.baseIRI.substring(0, this.props.baseIRI.indexOf('#'))
            :
            this.props.baseIRI;
        const statementIRI = baseIRI + slashHash + uuidv4();
        let newStatement = {
            id: statementIRI,
            type: "Statement",
            rdfSubject: this.props.subject,
            rdfPredicate: this.props.property,
            rdfObject: this.props.value
        };
        // We need to make sure the subject is aware of this as it will be loaded immediately (so before hits DB maybe)
        await GS.insertResource(newStatement, statementIRI, this.props.graph, [this.props.subject]);
        return newStatement;
    };

    render() {

        const { isReadOnly, defaultLang, projectLang, linkedData, ontologyProjectAssociation,
            viewLang, graph, propertyTemplates, baseIRI, dialogComponent,
            projectIRI, componentIRI, useWorkflow, associatedTaxonomyProject, showDialog, subject, property, value,
            components, projectType } = this.props;
        const { applicableStatements, isErrored } = this.state;

        if (isErrored) return <Errored dataTest="reification-error" data={isErrored} />;
        if (!applicableStatements) return null;

        return (
            <ReificationDialog
                subject={subject}
                property={property}
                requiredEntitlement={projectType === "TaxonomyProject" ?
                    ENTITLEMENTS.EDIT_TAXONOMY
                    :
                    ENTITLEMENTS.EDIT_DATASET}
                value={value}
                dialogComponent={dialogComponent}
                isReadOnly={isReadOnly}
                baseIRI={baseIRI}
                statements={applicableStatements}
                ontologyProjectAssociation={ontologyProjectAssociation}
                graph={graph}
                showDialog={showDialog}
                projectIRI={projectIRI}
                componentIRI={componentIRI}
                useWorkflow={useWorkflow}
                propertyTemplates={propertyTemplates}
                viewLang={viewLang}
                projectLang={projectLang}
                projectType={projectType}
                linkedData={linkedData}
                associatedTaxonomyProject={associatedTaxonomyProject}
                components={components}
                defaultLang={defaultLang} />
        );
    }
}

ReificationContainer.defaultProps = {
    statements: [],
    isReadOnly: false,
    baseIRI: "https://grafsync.graphifi.com/data/"
};

ReificationContainer.propTypes = {
    projectIRI: PropTypes.string.isRequired,
    projectType: PropTypes.string.isRequired,
    projectLang: PropTypes.array.isRequired,
    defaultLang: PropTypes.string.isRequired,
    associatedTaxonomyProject: PropTypes.array,
    baseIRI: PropTypes.string,
    isReadOnly: PropTypes.bool,
    showDialog: PropTypes.func,
    componentIRI: PropTypes.string,
    statements: PropTypes.array,
    classes: PropTypes.object
};

export default withStyles(styles)(ReificationContainer);